/**
 * NavWidget — the "block menu": links to the site's pages. Lives (locked) in the
 * shared header/footer, so it appears identically on every page. Links are
 * DERIVED from the site's pages (the Yjs `nodes` map), so adding/renaming a page
 * updates the menu everywhere automatically. Display-only on the canvas (the
 * hrefs don't navigate the builder); publish emits the real slugs.
 *
 * Locked: seeded with `constraints.noDelete` so its block chrome offers no
 * delete — you can restyle/move it, but not remove it (a 🔒 marks it).
 */
import { useEffect, useState } from "react";
import { html, css } from "react-strict-dom";
import { spacing } from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { useEditorContext } from "../EditorContext.js";
import { childrenOf } from "../pageHierarchy.js";
import type { WidgetProps } from "./types.js";

const styles = css.create({
  nav: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.lg,
    padding: spacing.xs,
  },
  lock: { fontSize: typography.fontSizeXs, opacity: 0.5 },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "row",
    gap: spacing.lg,
    flexWrap: "wrap",
  },
  link: { textDecoration: "none", color: "inherit" },
});

interface PageLink {
  id: string;
  label: string;
}

/**
 * The top-level menu: the homepage + its DIRECT children only — not the
 * grandchildren (a child page's own children belong to that page's sub-nav,
 * not the site menu). Home = the "home" node, else the first root (no incoming
 * edge), else the first node — matching the publish slug resolution.
 */
function readNav(
  ydoc: ReturnType<typeof useEditorContext>["ydoc"],
): PageLink[] {
  const nodes = ydoc.getMap("nodes");
  const ids = [...nodes.keys()];
  if (ids.length === 0) return [];
  const edges = [...ydoc.getMap("edges").values()] as {
    id: string;
    source: string;
    target: string;
  }[];
  const labelOf = (id: string): string => {
    const data = (nodes.get(id) as { data?: { label?: unknown } })?.data;
    return String(data?.label ?? id);
  };
  const incoming = new Set(edges.map((e) => e.target));
  const home =
    ids.find((id) => id === "home") ??
    ids.find((id) => !incoming.has(id)) ??
    ids[0]!;
  return [home, ...childrenOf(edges, home)].map((id) => ({
    id,
    label: labelOf(id),
  }));
}

export function NavWidget(_props: WidgetProps) {
  const { ydoc } = useEditorContext();
  const [pages, setPages] = useState<PageLink[]>(() => readNav(ydoc));
  useEffect(() => {
    // Depends on both pages (labels) and edges (hierarchy).
    const nodesMap = ydoc.getMap("nodes");
    const edgesMap = ydoc.getMap("edges");
    const update = () => setPages(readNav(ydoc));
    nodesMap.observeDeep(update);
    edgesMap.observeDeep(update);
    update();
    return () => {
      nodesMap.unobserveDeep(update);
      edgesMap.unobserveDeep(update);
    };
  }, [ydoc]);

  return (
    <html.nav style={styles.nav}>
      <html.span aria-hidden style={styles.lock}>
        🔒
      </html.span>
      <html.ul style={styles.list}>
        {pages.map((p) => (
          <html.li key={p.id}>
            <html.a
              href={`#${p.id}`}
              // Builder-only: links don't navigate the canvas.
              onClick={(e) => e.preventDefault()}
              style={styles.link}
            >
              {p.label}
            </html.a>
          </html.li>
        ))}
      </html.ul>
    </html.nav>
  );
}
